import type {
  AuthSession,
  BillingOverview,
  BillingRunResult,
  Booking,
  BookingInput,
  BookingStatusInput,
  Business,
  BusinessDocument,
  BusinessUpdate,
  CardStats,
  Client,
  ClientBilling,
  ClientInput,
  ClientRequest,
  ClientUpdate,
  CleaningLog,
  ComplianceSummary,
  ConsentForm,
  ConsentSignInput,
  ConsentTemplate,
  Credit,
  DailyRegister,
  Dog,
  DogInput,
  DogUpdate,
  FeedItem,
  HealthItem,
  HealthItemInput,
  Holiday,
  HolidayImpact,
  HolidayInput,
  HolidayResult,
  Incident,
  IncidentInput,
  InspectionPack,
  InspectionPackInput,
  Invoice,
  LoginInput,
  Me,
  Member,
  MemberInput,
  MemberUpdate,
  OwnerBilling,
  OwnerDog,
  OwnerFeed,
  OwnerForms,
  PublicCard,
  PublicInvoice,
  MarkPaidInput,
  PublicConsent,
  RegisterInput,
  ReportCard,
  ReportCardUpdate,
  Schedule,
  ScheduleBulkInput,
  ScheduleInput,
  ScheduleUpdate,
  Service,
  ServiceInput,
  ServiceUpdate,
  Today,
  UploadRequest,
  UploadTicket,
  Walk,
} from '@walkkit/shared';
import { createApiClient, type QueryValue } from './client';
import { createSessionStore } from './session';
import type {
  BillingRunBody,
  BookingUpdateBody,
  BookingsQueryParams,
  BusinessDocumentBody,
  CardMessageDto,
  CardReplyBody,
  CleaningLogBody,
  ConfirmUploadResultDto,
  ConsentRequestBody,
  ConsentTemplateBody,
  CreditBody,
  HealthItemUpdateBody,
  IncidentUpdateBody,
  InvoicesQueryParams,
  Items,
  LinkResultDto,
  OwnerRequestsDto,
  Page,
  ReportCardsQueryParams,
} from './types';

export { ApiError, errorTitle, isApiError } from './errors';
export type { StoredSession } from './session';
export { activeMembership, toStoredSession } from './session';

const storage = typeof window !== 'undefined' ? window.localStorage : null;
export const sessionStore = createSessionStore(storage);

const authLostListeners = new Set<() => void>();
export function onAuthLost(fn: () => void): () => void {
  authLostListeners.add(fn);
  return () => authLostListeners.delete(fn);
}

export const http = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? '',
  store: sessionStore,
  onAuthLost: () => authLostListeners.forEach((l) => l()),
});

const enc = encodeURIComponent;
type Q = Record<string, QueryValue>;
const q = (o: object | undefined): Q | undefined => o as Q | undefined;

/** Follow cursors so small businesses (≤ a few hundred rows) get the whole list. */
async function all<T>(path: string, query: Q = {}, maxPages = 10): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | null | undefined;
  for (let i = 0; i < maxPages; i++) {
    const page = await http.get<Page<T>>(path, { query: { ...query, limit: 200, cursor } });
    out.push(...page.items);
    cursor = page.nextCursor;
    if (!cursor) break;
  }
  return out;
}

export const api = {
  auth: {
    login: (body: LoginInput) => http.post<AuthSession>('/auth/login', body, { auth: false }),
    register: (body: RegisterInput) => http.post<AuthSession>('/auth/register', body, { auth: false }),
    magicLink: (body: { email: string }) => http.post<{ ok: true }>('/auth/magic-link', body, { auth: false }),
    verifyMagicLink: (body: { token: string } | { email: string; code: string }) =>
      http.post<AuthSession>('/auth/magic-link/verify', body, { auth: false }),
    logout: (refreshToken: string) => http.post<{ ok: true }>('/auth/logout', { refreshToken }, { tenant: false }),
    me: () => http.get<Me>('/me', { tenant: false }),
  },

  business: {
    get: () => http.get<Business>('/business'),
    update: (body: BusinessUpdate) => http.patch<Business>('/business', body),
    setLogo: (blobKey: string) => http.post<Business>('/business/logo', { blobKey }),
    documents: () => http.get<BusinessDocument[]>('/business/documents'),
    addDocument: (body: BusinessDocumentBody) => http.post<BusinessDocument>('/business/documents', body),
    deleteDocument: (id: string) => http.del<void>(`/business/documents/${enc(id)}`),
  },

  members: {
    list: () => http.get<Items<Member>>('/members'),
    invite: (body: MemberInput) => http.post<Member>('/members', body),
    update: (id: string, body: MemberUpdate) => http.patch<Member>(`/members/${enc(id)}`, body),
    remove: (id: string) => http.del<void>(`/members/${enc(id)}`),
  },

  clients: {
    list: (query?: { status?: string }) => all<Client>('/clients', q(query)),
    get: (id: string) => http.get<Client>(`/clients/${enc(id)}`),
    create: (body: ClientInput) => http.post<Client>('/clients', body),
    update: (id: string, body: ClientUpdate) => http.patch<Client>(`/clients/${enc(id)}`, body),
    remove: (id: string) => http.del<void>(`/clients/${enc(id)}`),
    invite: (id: string) => http.post<unknown>(`/clients/${enc(id)}/invite`),
    billing: (id: string) => http.get<ClientBilling>(`/clients/${enc(id)}/billing`),
  },

  dogs: {
    list: (query?: { clientId?: string; active?: boolean }) => all<Dog>('/dogs', q(query)),
    get: (id: string) => http.get<Dog>(`/dogs/${enc(id)}`),
    create: (body: DogInput) => http.post<Dog>('/dogs', body),
    update: (id: string, body: DogUpdate) => http.patch<Dog>(`/dogs/${enc(id)}`, body),
    remove: (id: string) => http.del<void>(`/dogs/${enc(id)}`),
    healthItems: (id: string) => http.get<HealthItem[]>(`/dogs/${enc(id)}/health-items`),
    addHealthItem: (id: string, body: HealthItemInput) => http.post<HealthItem>(`/dogs/${enc(id)}/health-items`, body),
    updateHealthItem: (id: string, itemId: string, body: HealthItemUpdateBody) =>
      http.patch<HealthItem>(`/dogs/${enc(id)}/health-items/${enc(itemId)}`, body),
    deleteHealthItem: (id: string, itemId: string) => http.del<void>(`/dogs/${enc(id)}/health-items/${enc(itemId)}`),
  },

  services: {
    list: () => http.get<Items<Service>>('/services'),
    create: (body: ServiceInput) => http.post<Service>('/services', body),
    update: (id: string, body: ServiceUpdate) => http.patch<Service>(`/services/${enc(id)}`, body),
    remove: (id: string) => http.del<void>(`/services/${enc(id)}`),
  },

  media: {
    uploadUrls: (body: UploadRequest) => http.post<UploadTicket[]>('/media/upload-urls', body),
    confirm: (blobKeys: string[]) => http.post<ConfirmUploadResultDto>('/media/confirm', { blobKeys }),
  },

  schedules: {
    list: (query?: { dogId?: string }) => http.get<Items<Schedule>>('/schedules', { query: q(query) }),
    create: (body: ScheduleInput) => http.post<Schedule>('/schedules', body),
    bulk: (body: ScheduleBulkInput) => http.post<Items<Schedule>>('/schedules/bulk', body),
    update: (id: string, body: ScheduleUpdate) => http.patch<Schedule>(`/schedules/${enc(id)}`, body),
    remove: (id: string) => http.del<void>(`/schedules/${enc(id)}`),
    end: (id: string, endDate: string) => http.post<Schedule>(`/schedules/${enc(id)}/end`, { endDate }),
  },

  bookings: {
    list: (query: BookingsQueryParams) => http.get<Items<Booking>>('/bookings', { query: q(query) }),
    create: (body: BookingInput) => http.post<Booking>('/bookings', body),
    update: (id: string, body: BookingUpdateBody) => http.patch<Booking>(`/bookings/${enc(id)}`, body),
    setStatus: (id: string, body: BookingStatusInput) => http.patch<Booking>(`/bookings/${enc(id)}/status`, body),
  },

  today: (date?: string) => http.get<Today>('/today', { query: { date } }),

  holidays: {
    list: () => http.get<Items<Holiday>>('/holidays'),
    preview: (body: HolidayInput) => http.post<HolidayImpact>('/holidays/preview', body),
    create: (body: HolidayInput) => http.post<HolidayResult>('/holidays', body),
    remove: (id: string) => http.del<void>(`/holidays/${enc(id)}`),
  },

  walks: {
    get: (id: string) => http.get<Walk>(`/walks/${enc(id)}`),
  },

  cards: {
    list: (query: ReportCardsQueryParams) => http.get<Page<ReportCard>>('/report-cards', { query: q(query) }),
    get: (id: string) => http.get<ReportCard>(`/report-cards/${enc(id)}`),
    update: (id: string, body: ReportCardUpdate) => http.patch<ReportCard>(`/report-cards/${enc(id)}`, body),
    resend: (id: string) => http.post<ReportCard>(`/report-cards/${enc(id)}/resend`),
    send: (id: string) => http.post<ReportCard>(`/report-cards/${enc(id)}/send`),
    revokeLink: (id: string) => http.post<ReportCard>(`/report-cards/${enc(id)}/revoke-link`),
    stats: (date?: string) => http.get<CardStats>('/report-cards/stats', { query: { date } }),
  },

  incidents: {
    list: (query?: { cursor?: string }) => http.get<Page<Incident>>('/incidents', { query: q({ limit: 100, ...query }) }),
    get: (id: string) => http.get<Incident>(`/incidents/${enc(id)}`),
    create: (body: IncidentInput) => http.post<Incident>('/incidents', body),
    update: (id: string, body: IncidentUpdateBody) => http.patch<Incident>(`/incidents/${enc(id)}`, body),
    notifyOwner: (id: string) => http.post<Incident>(`/incidents/${enc(id)}/notify-owner`),
  },

  cleaning: {
    list: (date: string) => http.get<Items<CleaningLog>>('/cleaning-logs', { query: { date } }),
    create: (body: CleaningLogBody) => http.post<CleaningLog>('/cleaning-logs', body),
    remove: (id: string) => http.del<void>(`/cleaning-logs/${enc(id)}`),
  },

  registers: {
    get: (date: string) => http.get<DailyRegister>('/registers', { query: { date } }),
  },

  compliance: {
    summary: () => http.get<ComplianceSummary>('/compliance/summary'),
  },

  consents: {
    templates: () => http.get<ConsentTemplate[]>('/consents/templates'),
    saveTemplate: (body: ConsentTemplateBody) => http.post<ConsentTemplate>('/consents/templates', body),
    list: (query?: { clientId?: string }) => http.get<ConsentForm[]>('/consents', { query: q(query) }),
    request: (body: ConsentRequestBody) => http.post<ConsentForm[]>('/consents', body),
    sendLink: (id: string) => http.post<unknown>(`/consents/${enc(id)}/send-link`),
    pdf: (id: string) => http.get<LinkResultDto>(`/consents/${enc(id)}/pdf`),
  },

  inspectionPacks: {
    list: () => http.get<InspectionPack[]>('/inspection-packs'),
    get: (id: string) => http.get<InspectionPack>(`/inspection-packs/${enc(id)}`),
    create: (body: InspectionPackInput) => http.post<InspectionPack>('/inspection-packs', body),
  },

  billing: {
    overview: () => http.get<BillingOverview>('/billing/overview'),
    connectOnboard: () => http.post<LinkResultDto>('/billing/connect-onboard'),
    mandateLink: (clientId: string) => http.post<LinkResultDto>('/billing/mandate-link', { clientId }),
    run: (body: BillingRunBody) => http.post<BillingRunResult>('/billing/run', body),
  },

  invoices: {
    list: (query: InvoicesQueryParams) => http.get<Page<Invoice>>('/invoices', { query: q(query) }),
    get: (id: string) => http.get<Invoice>(`/invoices/${enc(id)}`),
    pdf: (id: string) => http.get<LinkResultDto>(`/invoices/${enc(id)}/pdf`),
    retry: (id: string) => http.post<{ ok: true }>(`/invoices/${enc(id)}/retry`),
    markPaid: (id: string, body: MarkPaidInput) => http.post<{ ok: true }>(`/invoices/${enc(id)}/mark-paid`, body),
    markUnpaid: (id: string) => http.post<{ ok: true }>(`/invoices/${enc(id)}/mark-unpaid`),
  },

  credits: {
    list: (query?: { clientId?: string }) => http.get<Items<Credit>>('/credits', { query: q(query) }),
    create: (body: CreditBody) => http.post<Credit>('/credits', body),
  },

  requests: {
    list: (query?: { status?: string }) => http.get<Items<ClientRequest>>('/requests', { query: q(query) }),
    approve: (id: string) => http.post<ClientRequest>(`/requests/${enc(id)}/approve`),
    decline: (id: string) => http.post<ClientRequest>(`/requests/${enc(id)}/decline`),
  },

  owner: {
    feed: (cursor?: string) => http.get<OwnerFeed>('/me/feed', { query: { cursor }, tenant: false }),
    card: (id: string) => http.get<FeedItem>(`/me/cards/${enc(id)}`, { tenant: false }),
    react: (id: string) => http.post<unknown>(`/me/cards/${enc(id)}/react`, {}, { tenant: false }),
    reply: (id: string, body: CardReplyBody) => http.post<unknown>(`/me/cards/${enc(id)}/reply`, body, { tenant: false }),
    dogs: () => http.get<OwnerDog[]>('/me/dogs', { tenant: false }),
    billing: () => http.get<OwnerBilling>('/me/billing', { tenant: false }),
    mandateLink: (businessId: string) =>
      http.post<LinkResultDto>(`/me/billing/${enc(businessId)}/mandate-link`, undefined, { tenant: false }),
    forms: () => http.get<OwnerForms>('/me/forms', { tenant: false }),
    requests: () => http.get<OwnerRequestsDto>('/me/requests', { tenant: false }),
  },

  /** No auth; token pages. */
  public: {
    card: (token: string) => http.get<PublicCard>(`/p/card/${enc(token)}`, { auth: false }),
    react: (token: string) => http.post<{ hearted: boolean }>(`/p/card/${enc(token)}/react`, {}, { auth: false }),
    reply: (token: string, body: CardReplyBody) =>
      http.post<CardMessageDto | { ok: true }>(`/p/card/${enc(token)}/reply`, body, { auth: false }),
    consent: (token: string) => http.get<PublicConsent>(`/p/consent/${enc(token)}`, { auth: false }),
    signConsent: (token: string, body: ConsentSignInput) =>
      http.post<{ ok: true }>(`/p/consent/${enc(token)}`, body, { auth: false }),
    mandateDone: (token: string) => http.get<unknown>(`/p/mandate/${enc(token)}/done`, { auth: false }),
    invoice: (token: string) => http.get<PublicInvoice>(`/p/invoice/${enc(token)}`, { auth: false }),
    payInvoice: (token: string) => http.post<LinkResultDto>(`/p/invoice/${enc(token)}/pay`, undefined, { auth: false }),
  },
};

export type Api = typeof api;
